import { createContext, useCallback, useContext, useEffect, useState } from "react";
import ChatPanel from "@/components/ChatPanel";
import { useAuth } from "@/lib/auth";

const ChatCtx = createContext(null);
const STORAGE_KEY = "bb_active_chat";

// Global floating chat — persists across route changes AND full page loads,
// Messenger-style. The open conversation is mirrored to sessionStorage.
export function ChatProvider({ children }) {
  const { user, loading } = useAuth();
  const [chat, setChat] = useState(null); // { listingId, otherName }

  // After auth resolves: restore any open chat, or drop it when logged out
  useEffect(() => {
    if (loading) return;
    if (!user) {
      try { sessionStorage.removeItem(STORAGE_KEY); } catch {}
      setChat((c) => (c ? null : c));
      return;
    }
    setChat((c) => {
      if (c) return c;
      try {
        const raw = sessionStorage.getItem(STORAGE_KEY);
        return raw ? JSON.parse(raw) : c;
      } catch { return c; }
    });
  }, [user, loading]);

  const openChat = useCallback((listingId, otherName, conversationId = null) => {
    const next = { listingId, otherName, conversationId, minimized: false };
    try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch {}
    setChat(next);
  }, []);

  const setChatMinimized = useCallback((v) => {
    setChat((c) => {
      if (!c) return c;
      const next = { ...c, minimized: v };
      try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch {}
      return next;
    });
  }, []);

  const closeChat = useCallback(() => {
    try { sessionStorage.removeItem(STORAGE_KEY); } catch {}
    setChat(null);
  }, []);

  return (
    <ChatCtx.Provider value={{ chat, openChat, closeChat }}>
      {children}
      {chat && user && (
        <ChatPanel
          key={chat.conversationId || chat.listingId}
          listingId={chat.listingId}
          conversationId={chat.conversationId}
          otherName={chat.otherName}
          minimized={!!chat.minimized}
          onMinimizeChange={setChatMinimized}
          onClose={closeChat}
        />
      )}
    </ChatCtx.Provider>
  );
}

export const useChat = () => useContext(ChatCtx);
